import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { SpinnerIcon } from '@phosphor-icons/react';
import type { APInvoice } from '@erp/rpc';
import { useReevaluateAPInvoiceExceptions } from '@/api/rpc/hooks/accounts-payable/useReevaluateAPInvoiceExceptions';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ExceptionRuleSetPicker } from './ExceptionRuleSetPicker';
import { errorText } from './testExceptionRules';
import {
  liveReevaluateOutcomeFromResult,
  type LiveReevaluateOutcome,
} from './invoiceExceptionsMenuState';

/**
 * Live evaluation confirm + progress for one invoice. The slide-in menu
 * opens this so the clerk sees the rule set, a spinner while the model
 * runs, and the outcome — the menu itself closes too quickly to show any
 * of that.
 */
export function ReevaluateExceptionsDialog({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: APInvoice;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [running, setRunning] = useState(false);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && running) return;
        onOpenChange(next);
      }}
      disableEscapeKeyClose={running}
      disableBackdropClickClose={running}
    >
      <DialogContent className="max-w-lg">
        {open && (
          <ReevaluateExceptionsBody
            invoice={invoice}
            onClose={() => onOpenChange(false)}
            onRunningChange={setRunning}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReevaluateExceptionsBody({
  invoice,
  onClose,
  onRunningChange,
}: {
  invoice: APInvoice;
  onClose: () => void;
  onRunningChange: (running: boolean) => void;
}) {
  const gt = useGT();
  const reevaluate = useReevaluateAPInvoiceExceptions({ notify: false });
  const [ruleSetId, setRuleSetId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<LiveReevaluateOutcome>({
    kind: 'idle',
  });
  const invoiceLabel = invoice.invoiceNumber ?? invoice.id.slice(0, 8);
  const isRunning = outcome.kind === 'running' || reevaluate.isPending;

  async function run(id: string) {
    setOutcome({ kind: 'running' });
    onRunningChange(true);
    const next = await reevaluate
      .mutateAsync({
        invoiceIds: [invoice.id],
        exceptionRuleSetId: id,
      })
      .then(
        ({ results }): LiveReevaluateOutcome =>
          liveReevaluateOutcomeFromResult(results[0]) ?? {
            kind: 'error',
            message: gt('No result returned', {
              context: 'slide-in re-evaluate dialog: empty RPC response',
            }),
          },
      )
      .catch((error: unknown): LiveReevaluateOutcome => ({
        kind: 'error',
        message: errorText(error),
      }));
    setOutcome(next);
    onRunningChange(false);
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="dialog title: live exception evaluation for one invoice">
            Re-evaluate Exceptions
          </T>
        </DialogTitle>
        <DialogDescription>
          <T context="dialog description: live exception evaluation is persisted">
            Live run against invoice <Var>{invoiceLabel}</Var>. Exceptions that
            apply will open; ones that no longer apply will resolve. Matching is
            not repeated.
          </T>
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <ExceptionRuleSetPicker
          ruleSetId={ruleSetId}
          isRunning={isRunning}
          onChange={setRuleSetId}
          onRun={(id) => void run(id)}
          copy={{
            fieldLabel: (
              <T context="label for the rule set picker in the live re-evaluate dialog">
                Rule set
              </T>
            ),
            loadingPlaceholder: gt('Loading rule sets…', {
              context: 'live re-evaluate dialog: rule set picker loading',
            }),
            selectPlaceholder: gt('Select a rule set…', {
              context: 'live re-evaluate dialog: rule set picker placeholder',
            }),
            searchPlaceholder: gt('Search rule sets…', {
              context: 'live re-evaluate dialog: rule set picker search',
            }),
            emptyHint: (
              <T context="live re-evaluate dialog: no rule sets exist yet">
                No rule sets yet — create one under Accounts Payable → Exception
                Rules.
              </T>
            ),
            runBusy: (
              <T context="live re-evaluate dialog: run button while running">
                Evaluating…
              </T>
            ),
            runIdle: <T context="live re-evaluate dialog: run button">Run</T>,
          }}
        />
        <LiveOutcomeBanner outcome={outcome} />
        {outcome.kind !== 'idle' && outcome.kind !== 'running' && (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={onClose}>
              <T context="slide-in re-evaluate dialog: dismiss after a result">
                Close
              </T>
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

function LiveOutcomeBanner({ outcome }: { outcome: LiveReevaluateOutcome }) {
  if (outcome.kind === 'idle') return null;
  if (outcome.kind === 'running') {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-3 text-sm">
        <SpinnerIcon className="size-4 animate-spin" />
        <T context="live re-evaluate dialog: in-progress status">
          Evaluating this invoice — this can take a little while.
        </T>
      </div>
    );
  }
  if (outcome.kind === 'error') {
    return (
      <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
        {outcome.message}
      </p>
    );
  }
  return (
    <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm dark:border-green-900 dark:bg-green-950/30">
      <T context="live re-evaluate dialog: success counts">
        Done. <Var>{outcome.openCount}</Var> exception(s) still open ·{' '}
        <Var>{outcome.openedCount}</Var> opened ·{' '}
        <Var>{outcome.resolvedCount}</Var> resolved.
      </T>
    </div>
  );
}
